<!-- Base.Input.vue (client-lms) -->
<template>
  <div
    class="lms_input_wrap"
    :class="{
      'lms_input_error': error,
      'lms_input_disabled': disabled,
      'lms_input_has_count': count
    }"
  >
    <input
      ref="baseInput"
      :type="type"
      class="lms_input"
      :class="{ 'border_none': borderNone }"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :maxlength="maxLength || undefined"
      v-model="model"
      @input="onInput"
      @change="onChange"
    />

    <span v-if="count" class="lms_input_count">
      {{ String(model ?? '').length }}/{{ maxLength }}
    </span>

    <span v-if="error && errorText" class="lms_input_error_txt">{{ errorText }}</span>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";

const model = defineModel<string | number | null>({ required: true, default: null });

const emit = defineEmits<{
  (e: "input", value: string | number | null): void;
  (e: "change", value: string | number | null): void;
}>();

const props = defineProps({
  placeholder: { type: String, default: "" },
  type: { type: String, default: "text" },
  error: { type: Boolean, default: false },
  errorText: { type: String, default: "" },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  borderNone: { type: Boolean, default: false },
  maxLength: { type: [Number, String], default: undefined },
  count: { type: Boolean, default: false },
});

const baseInput = ref<HTMLInputElement>();

const onInput = (e: Event) => {
  emit("input", (e.target as HTMLInputElement).value);
};

const onChange = (e: Event) => {
  emit("change", (e.target as HTMLInputElement).value);
};

const focus = () => {
  baseInput.value?.focus();
};

defineExpose({ focus });
</script>

<style scoped>
.lms_input_wrap {
  position: relative;
  display: inline-flex;
  flex-direction: column;
}

.lms_input {
  height: 48px;
  padding: 8px 12px;
  border: 1px solid var(--lms-outline-gray-main, #E0E0E0);
  border-radius: 4px;
  background-color: var(--lms-surface, #FFFFFF);
  font-family: "Pretendard", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--lms-on-surface-high, rgba(0, 0, 0, 0.8));
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
}

.lms_input::placeholder {
  color: var(--lms-on-surface-low, rgba(0, 0, 0, 0.35));
}

.lms_input:hover {
  border-color: var(--lms-outline-black, #AAABAB);
}

.lms_input:focus {
  border-color: var(--lms-primary-400, #5C74F6);
  border-width: 2px;
  padding: 7px 11px;
}

.lms_input:disabled {
  background-color: var(--lms-grey-100, #F6F7F7);
  color: var(--lms-on-surface-low-var, rgba(0, 0, 0, 0.18));
  cursor: not-allowed;
}

.lms_input.border_none {
  border: none;
}

.lms_input_error .lms_input {
  border-color: var(--lms-outline-error, #FF1A2D);
}

.lms_input_error_txt {
  margin-top: 4px;
  font-size: 12px;
  color: var(--lms-outline-error, #FF1A2D);
}

.lms_input_has_count .lms_input {
  padding-right: 60px;
}

.lms_input_has_count .lms_input:focus {
  padding-right: 59px;
}

.lms_input_count {
  position: absolute;
  right: 12px;
  top: 0;
  height: 48px;
  display: flex;
  align-items: center;
  font-size: 16px;
  line-height: 24px;
  color: var(--lms-on-surface-low, rgba(0, 0, 0, 0.35));
  pointer-events: none;
}
</style>
